
/*import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProductCategory, ProductSubcategory } from '@/types/product';

export const useProductCategories = () => {
  const { data: categories = [], isLoading, error, refetch } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select(`
          *,
          product_subcategories (*)
        `)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      return data as (ProductCategory & { product_subcategories: ProductSubcategory[] })[];
    },
  });

  return {
    categories,
    isLoading,
    error,
    refetch
  };
};

export const useProductSubcategories = (categoryId?: string) => {
  const { data: subcategories = [], isLoading, error, refetch } = useQuery({
    queryKey: ['product-subcategories', categoryId],
    queryFn: async () => {
      let query = supabase
        .from('product_subcategories')
        .select('*');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      query = query.order('display_order', { ascending: true, nullsFirst: false });

      const { data, error } = await query;
      if (error) throw error;
      return data as ProductSubcategory[];
    },
  });

  return {
    subcategories,
    isLoading,
    error,
    refetch
  };
};
*/
// hooks/useProductCategories.ts - OPTIMIZED VERSION
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProductCategory, ProductSubcategory } from '@/types/product';

export const useProductCategories = () => {
  const { data: categories = [], isLoading, error, refetch } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .limit(20);

      if (error) throw error;
      return data as ProductCategory[];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { categories, isLoading, error, refetch };
};

// NEW: Hook for categories with subcategories (only when needed)
export const useProductCategoriesWithSubs = () => {
  // Delay this query past the immediate first-render burst instead of firing it instantly —
  // it's used by the nav (desktop + mobile), homepage category cards, and admin category
  // management, all of which already degrade gracefully (nav has a static fallback list,
  // homepage cards show a skeleton, admin shows a normal loading state). Same
  // requestIdleCallback + setTimeout-fallback pattern proven in production for the category
  // counts RPC (S54 step 4), but with a much shorter 800ms bound since this is real,
  // clickable nav data rather than decorative content — just enough to clear the earliest
  // resource-loading burst without letting the fallback/stale window drag out.
  const [idleReady, setIdleReady] = useState(false);
  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setIdleReady(true), { timeout: 800 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(() => setIdleReady(true), 800);
    return () => window.clearTimeout(timer);
  }, []);

  const { data: categories = [], isLoading, error, refetch } = useQuery({
    queryKey: ['product-categories-with-subs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select(`
          *,
          product_subcategories (*)
        `)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      return data as (ProductCategory & { product_subcategories: ProductSubcategory[] })[];
    },
    enabled: idleReady,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { categories, isLoading, error, refetch };
};

// NEW: Separate hook for subcategories when needed
export const useProductSubcategories = (categoryId?: string) => {
  const { data: subcategories = [], isLoading, isSuccess, isFetching, error, refetch } = useQuery({
    queryKey: ['product-subcategories', categoryId],
    queryFn: async () => {
      let query = supabase
        .from('product_subcategories')
        .select('*');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      query = query.order('display_order', { ascending: true, nullsFirst: false });

      const { data, error } = await query;
      if (error) throw error;
      return data as ProductSubcategory[];
    },
    enabled: categoryId === undefined || !!categoryId, // Run always if categoryId is undefined, or when categoryId exists
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { subcategories, isLoading, isSuccess, isFetching, error, refetch };
};
